/**
 * ExportModal — Exporta los casos de prueba de un escenario a Excel o Word.
 *
 * Excel usa el mismo formato de columnas que ExcelImportModal (re-importable).
 * Word genera un documento formal (portada, contexto, resumen, detalle por caso).
 * Ambos formatos se generan en el backend a partir del mismo árbol de datos.
 */
import React, { useState } from 'react';
import { scenarios as scenariosApi } from '../../services/api';

const OVERLAY: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
  zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const MODAL: React.CSSProperties = {
  width: 460, borderRadius: 16,
  background: 'var(--color-background-primary)',
  border: '1px solid var(--color-border-secondary)',
  display: 'flex', flexDirection: 'column', overflow: 'hidden',
};
const BTN_PRIMARY: React.CSSProperties = {
  padding: '9px 20px', borderRadius: 8, border: 'none',
  background: '#3B8BD4', color: '#fff', cursor: 'pointer',
  fontSize: 13, fontWeight: 600,
};
const BTN_SECONDARY: React.CSSProperties = {
  padding: '9px 16px', borderRadius: 8,
  border: '1px solid var(--color-border-secondary)',
  background: 'transparent', cursor: 'pointer',
  fontSize: 13, color: 'var(--color-text-secondary)',
};

interface ExportModalProps {
  scenarioId: string;
  scenarioTitle: string;
  caseCount: number;
  onClose: () => void;
}

const FORMATS = [
  { id: 'xlsx' as const, icon: '📊', label: 'Excel (.xlsx)', desc: 'Mismo formato usado para importar casos — re-importable.' },
  { id: 'docx' as const, icon: '📄', label: 'Word (.docx)', desc: 'Documento formal: portada, contexto y detalle por caso.' },
];

export function ExportModal({ scenarioId, scenarioTitle, caseCount, onClose }: ExportModalProps) {
  const [format, setFormat] = useState<'xlsx' | 'docx'>('xlsx');
  const [status, setStatus] = useState<'idle' | 'exporting' | 'done' | 'error'>('idle');

  const safeName = scenarioTitle.replace(/[^a-zA-Z0-9 _-]/g, '_').trim() || 'escenario';

  const handleExport = async () => {
    setStatus('exporting');
    try {
      await scenariosApi.export(scenarioId, format, `${safeName}.${format}`);
      setStatus('done');
    } catch {
      setStatus('error');
    }
  };

  const handleSelectFormat = (id: 'xlsx' | 'docx') => {
    setFormat(id);
    if (status === 'done' || status === 'error') setStatus('idle');
  };

  return (
    <div style={OVERLAY} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={MODAL}>
        <div style={{ padding: '16px 22px', borderBottom: '1px solid var(--color-border-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-text-primary)' }}>Exportar casos de prueba</div>
            <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>Escenario: {scenarioTitle} · {caseCount} caso{caseCount !== 1 ? 's' : ''}</div>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 18, cursor: 'pointer', color: 'var(--color-text-tertiary)' }}>✕</button>
        </div>

        <div style={{ padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {FORMATS.map(f => (
            <div
              key={f.id}
              onClick={() => handleSelectFormat(f.id)}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 14px', borderRadius: 10, cursor: 'pointer',
                border: `1px solid ${format === f.id ? '#3B8BD4' : 'var(--color-border-tertiary)'}`,
                background: format === f.id ? '#E6F1FB22' : 'transparent',
              }}
            >
              <span style={{ fontSize: 22 }}>{f.icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)' }}>{f.label}</div>
                <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>{f.desc}</div>
              </div>
              <div style={{ width: 16, height: 16, borderRadius: '50%', marginTop: 2, border: `2px solid ${format === f.id ? '#3B8BD4' : 'var(--color-border-secondary)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {format === f.id && <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#3B8BD4' }} />}
              </div>
            </div>
          ))}

          {status === 'error' && (
            <div style={{ padding: '10px 14px', borderRadius: 8, background: '#FCEBEB', border: '1px solid #F7C1C1', color: '#791F1F', fontSize: 12 }}>
              ⚠ No se pudo generar el archivo. Intenta de nuevo.
            </div>
          )}
          {status === 'done' && (
            <div style={{ padding: '10px 14px', borderRadius: 8, background: '#E1F5EE', border: '1px solid #9DE3C7', color: '#0F6E56', fontSize: 12 }}>
              ✓ Archivo descargado correctamente.
            </div>
          )}
        </div>

        <div style={{ padding: '14px 22px', borderTop: '1px solid var(--color-border-tertiary)', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={BTN_SECONDARY}>{status === 'done' ? 'Cerrar' : 'Cancelar'}</button>
          <button onClick={handleExport} disabled={status === 'exporting' || caseCount === 0} style={{ ...BTN_PRIMARY, opacity: status === 'exporting' || caseCount === 0 ? 0.5 : 1, cursor: status === 'exporting' || caseCount === 0 ? 'not-allowed' : 'pointer' }}>
            {status === 'exporting' ? 'Generando...' : '⬇ Exportar'}
          </button>
        </div>
      </div>
    </div>
  );
}
